import axios from 'axios';
import { appConfig } from 'config';

import { QuickApiProvider, QuickHttpStatusCode } from 'hooks/services';

console.log(
	`Initializing axios API using url: ${appConfig.backendUrl}`,
	process.env
);

const baseUrl = new URL(appConfig.backendUrl).href;

const axiosClient = axios.create({
	baseURL: baseUrl,
	maxBodyLength: Infinity,
	maxContentLength: Infinity,
});

/**
 * Get the OTP session token for a specific application from localStorage.
 */
export function getOtpToken(applicationId: string): string | null {
	try {
		const stored = localStorage.getItem(`otp_session_${applicationId}`);
		if (!stored) return null;
		const data = JSON.parse(stored);
		if (data.token && new Date(data.expiresAt) > new Date()) {
			return data.token;
		}
	} catch { /* ignore parse errors */ }
	return null;
}

/**
 * Extract applicationId from an axios request URL or body.
 * Supports: applications/.../id, chat/.../id, FormData with applicationId field.
 */
export function extractApplicationIdFromRequest(config: any): string | null {
	const url = config.url || '';

	// Match applications/get-by-id/{id} or applications/update/{id}.
	// Leading boundary is (start-of-string | slash) because buildUrl emits the
	// resource-prefixed path WITHOUT a leading slash (e.g. "applications/get-by-id/<id>").
	let match = url.match(/(?:^|\/)applications\/(?:get-by-id|update)\/([a-f0-9]{24})/);
	if (match) return match[1];

	// Match chat/add-message/{applicationId} (same leading-boundary rationale)
	match = url.match(/(?:^|\/)chat\/(?:add-message|messages|latest)\/([a-f0-9]{24})/);
	if (match) return match[1];

	// Check query params (e.g. ?applicationId=...)
	const queryMatch = url.match(/[?&]applicationId=([a-f0-9]{24})/);
	if (queryMatch) return queryMatch[1];

	// Check body for applicationId (FormData for file uploads, or plain object for chat)
	if (config.data instanceof FormData) {
		const appId = config.data.get('applicationId');
		if (appId && typeof appId === 'string') return appId;
	} else if (config.data && typeof config.data === 'object' && config.data.applicationId) {
		return config.data.applicationId;
	}

	return null;
}

// Attach OTP session token to every outgoing request
// Uses applicationId-specific lookup to support multiple concurrent sessions
axiosClient.interceptors.request.use((config) => {
	if (!config.headers.Authorization) {
		const applicationId = extractApplicationIdFromRequest(config);
		if (applicationId) {
			const token = getOtpToken(applicationId);
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			}
		}
		// No fallback — if applicationId can't be determined, request goes without token.
		// ExternalRequestsApi will return 401 with a clear "OTP required" message.
	}
	return config;
});

export const appApiProvider: QuickApiProvider = {
	updateJwtToken: (token: string): void => {
		if (token) {
			axiosClient.defaults.headers.common.Authorization = `Bearer ${token}`;
		} else {
			delete axiosClient.defaults.headers.common.Authorization;
		}
	},

	get: async <T>(
		url: string
	): Promise<{ data: T; httpStatusCode: QuickHttpStatusCode }> => {
		const result = await axiosClient.get(url);

		return { data: result.data, httpStatusCode: result.status };
	},

	post: async <T>(
		url: string,
		body: any
	): Promise<{ data: T; httpStatusCode: QuickHttpStatusCode }> => {
		const result = await axiosClient.post(url, body);

		return { data: result.data, httpStatusCode: result.status };
	},

	put: async <T>(
		url: string,
		body: any
	): Promise<{ data: T; httpStatusCode: QuickHttpStatusCode }> => {
		const result = await axiosClient.put(url, body);
		return { data: result.data, httpStatusCode: result.status };
	},

	delete: async <T>(
		url: string
	): Promise<{ data: T; httpStatusCode: QuickHttpStatusCode }> => {
		const result = await axiosClient.delete(url);

		return { data: result.data, httpStatusCode: result.status };
	},
	saveBlob: (data: Blob, fileName: string) => {
		throw new Error('Function not implemented.');
	},
};
